import {
  USER,
  AUTH,
  menuActions,
  NOTIFICATION,
  APP_SETTINGS,
  USER_PREFERENCES,
} from "../_constants/actionConstants";
import mainTabActions from "./mainTabActions";
import { userService } from "../_services/userService";
import axios from "axios";
import qs from "qs";
import { BASE_URL } from "../_constants/urlConstants";
import { getUUID } from "../_utils/generalUtils";
import { actionScripts } from "../_helpers/actionScripts";
import getDefaultUserPreferences from "../_helpers/defaultUserPreferences";
import raiseError from "./raiseError";
import raiseWarning from "./raiseWarning";

const login = (username, password) => async (dispatch) => {
  dispatch({ type: AUTH.LOGIN_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Authentication/NonSso`;
  const loginDto = { username, password };
  try {
    const res = await axios.post(apiEndpoint, loginDto);
    let userPreferences = getDefaultUserPreferences();
    if (res.data.userPreferences) {
      try {
        userPreferences = JSON.parse(res.data.userPreferences);
      } catch (e) {
        // If JSON.parse() fails, fallback is to reset.
      }
    }

    localStorage.setItem("prodms_token", res.data.token);
    userService.setAuthHeader();

    dispatch({
      type: AUTH.LOGIN_SUCCESS,
      user: { ...res.data.user, projectRoles: res.data.projectRoles, userPreferences },
    });
  } catch (e) {
    raiseError(AUTH.LOGIN_FAILURE, e.response.data);
  }
};

const getAppSettings = () => async (dispatch) => {
  const apiEndpoint = `${BASE_URL}/api/Authentication/AppSettings`;

  dispatch({ type: APP_SETTINGS.GET_APP_SETTINGS });
  try {
    const appSettingsRes = await axios.get(apiEndpoint);
    dispatch({
      type: APP_SETTINGS.GET_APP_SETTINGS_FULFILLED,
      version: appSettingsRes.data.version,
      environmentContext: appSettingsRes.data.environmentContext,
      useClientSidePDFGeneration: appSettingsRes.data.useClientSidePDFGeneration,
      implementationProgram: appSettingsRes.data.implementationProgram,
    });
  } catch (e) {}
};

/**
 * Main authentication gateway for the app.
 * - If the user is not logged in, direct the user to a login screen.
 *   (Depending on the environment, it is either the organizational SSO
 *   or ProDMS login box.)
 * - If the user is logged in, obtain the loaded userDto, set the relevant
 *   user data in localStorage, and configure the auth token in axios for
 *   future requests.
 * @returns
 */
const getAuth = () => async (dispatch, getState) => {
  dispatch({ type: AUTH.GET_AUTHORIZATION });

  try {
    const envContext = getState().appSettings.environmentContext;
    const existToken = localStorage.getItem("prodms_token");
    const apiEndpoint = `${BASE_URL}/api/Authentication/Get${
      existToken ? "?existToken=" + existToken : ""
    }`;

    const res = await axios.get(apiEndpoint);

    // If res.data exists, existing token is valid
    if (res.data) {
      let userPreferences = getDefaultUserPreferences();
      if (res.data.userPreferences) {
        try {
          userPreferences = JSON.parse(res.data.userPreferences);
        } catch (e) {
          // If JSON.parse() fails, fallback is to reset.
        }
      }

      localStorage.setItem("prodms_token", res.data.token);

      // Below two are only for native app use; timeout will remove them
      localStorage.setItem("userId", res.data.user.userId);
      localStorage.setItem("employeeNo", res.data.user.employeeNo);

      userService.setAuthHeader();
      dispatch({
        type: AUTH.LOGIN_SUCCESS,
        user: {
          ...res.data.user,
          projectRoles: res.data.projectRoles,
          userPreferences,
          mustRegister: res.data.mustCompleteRegistration,
        },
      });
    } else {
      // If !user, user remains unauthenticated unless:
      // - For CT/CT_UAT/Local_SSO environments, redirect to InitiateSSO
      // - For all others (including Azure UAT/prod environments), do nothing
      //   ("Login" button will be displayed.)
      if (["CT", "Local_SSO"].includes(envContext)) {
        window.location = BASE_URL + "/Saml/InitiateSingleSignOn?returnUrl=" + BASE_URL;
        return;
      }
    }

    return res.data.user;
  } catch (err) {
    console.error("error", err);
    const errorId = getUUID();
    const type = AUTH.LOGIN_FAILURE;
    const newError = { errorId, type, error: err?.response?.data };
    actionScripts.ADD_ERROR.run(newError);
  }
};

const logout = () => {
  return function (dispatch) {
    userService.logout();
    dispatch(mainTabActions.closeAllTabs());
    dispatch(mainTabActions.selectTab(0)); // Select the home tab
    // Reset menu to default
    dispatch({ type: menuActions.RESET_MENU_ON_LOGOUT });
    dispatch({ type: USER.CLEAR_ALL_USER_DATA });
    dispatch({ type: AUTH.LOGOUT });
  };
};

const add = (user) => async (dispatch) => {
  dispatch({ type: USER.REGISTER_USER });
  const apiEndpoint = `${BASE_URL}/api/Users/Register`;
  try {
    const res = await axios.post(apiEndpoint, user);
    dispatch({ type: USER.REGISTER_USER_FULFILLED, user: res.data });
    return res.data;
  } catch (err) {
    raiseError(USER.REGISTER_USER_REJECTED, err.response.data);
  }
};

const update =
  (user, currentUserId = null) =>
  async (dispatch) => {
    dispatch({ type: USER.EDIT_USER });
    const apiEndpoint = `${BASE_URL}/api/Users/${user.userId}`;
    try {
      const res = await axios.put(apiEndpoint, user);
      dispatch({
        type: USER.EDIT_USER_FULFILLED,
        user: res.data, // Updated user is returned
      });

      if (currentUserId && user.userId === currentUserId)
        dispatch({ type: USER.EDIT_USER_SELF, user: res.data });

      return res.data;
    } catch (err) {
      raiseError(USER.EDIT_USER_REJECTED, err.response.data);
    }
  };

const getName = (userId) => async (dispatch) => {
  dispatch({ type: USER.FETCH_NAME });
  const apiEndpoint = `${BASE_URL}/api/Users/${userId}/Name`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: USER.FETCH_NAME_FULFILLED });
    return res.data;
  } catch (e) {
    dispatch({ type: USER.FETCH_NAME_REJECTED });
    raiseError(USER.FETCH_NAME_REJECTED, e.response.data);
  }
};

const getSignature = (userId, suppressWarning) => async (dispatch) => {
  dispatch({ type: USER.FETCH_SIGNATURE });
  const apiEndpoint = `${BASE_URL}/api/UserSignature/ForUser/${userId}`;
  try {
    const res = await axios.get(apiEndpoint);
    if (res.data.message) {
      if (!suppressWarning) raiseWarning(USER.FETCH_SIGNATURE_REJECTED, res.data.message);
      else dispatch({ type: USER.FETCH_SIGNATURE_REJECTED });
      return;
    }

    dispatch({ type: USER.FETCH_SIGNATURE_FULFILLED, signature: res.data });
    return;
  } catch (e) {
    raiseError(USER.FETCH_NAME_REJECTED, e.response.data);
  }
};

const addSignature = (userId, userSignatureDto) => async (dispatch) => {
  dispatch({ type: USER.ADD_SIGNATURE });
  const apiEndpoint = `${BASE_URL}/api/UserSignature`;
  const signatureDto = {
    ...userSignatureDto,
    userId,
    userSignatureId: 0,
  };
  try {
    const res = await axios.post(apiEndpoint, signatureDto);
    if (res.data.message) {
      raiseWarning(USER.ADD_SIGNATURE_REJECTED, res.data.message);
      return;
    }

    dispatch({
      type: USER.ADD_SIGNATURE_FULFILLED,
      signature: userSignatureDto,
    });
  } catch (e) {
    raiseError(USER.ADD_SIGNATURE_REJECTED, e.response.error);
  }
};

const updateSignature = (userSignatureId, userSignatureDto) => async (dispatch) => {
  dispatch({ type: USER.UPDATE_SIGNATURE });
  const apiEndpoint = `${BASE_URL}/api/UserSignature/${userSignatureId}`;
  try {
    const res = await axios.put(apiEndpoint, userSignatureDto);
    if (res.data.message) {
      raiseWarning(USER.UPDATE_SIGNATURE_REJECTED, res.data.message);
      return;
    }

    dispatch({
      type: USER.UPDATE_SIGNATURE_FULFILLED,
      signature: userSignatureDto,
    });
    return;
  } catch (e) {
    raiseError(USER.UPDATE_SIGNATURE_REJECTED, e.response.data);
  }
};

const deleteSignature = (userSignatureId) => async (dispatch) => {
  dispatch({ type: USER.DELETE_SIGNATURE });
  const apiEndpoint = `${BASE_URL}/api/UserSignature/${userSignatureId}`;
  try {
    const res = await axios.delete(apiEndpoint);
    if (res.data.message) {
      raiseWarning(USER.DELETE_SIGNATURE_REJECTED, res.data.message);
      return;
    }

    dispatch({ type: USER.DELETE_SIGNATURE_FULFILLED });
    dispatch({ type: USER.CLEAR_SIGNATURE_DATA });
    return;
  } catch (e) {
    raiseError(USER.DELETE_SIGNATURE_REJECTED, e.response.data);
  }
};

const deleteProfilePic = (tabId, userId) => async (dispatch) => {
  dispatch({ type: USER.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Users/${userId}/ProfilePic`;
  try {
    const res = await axios.delete(apiEndpoint);
    if (res.data.message) {
      raiseWarning(USER.GENERIC_REQUEST_REJECTED, res.data.message);
      return;
    }

    dispatch({ type: USER.GENERIC_REQUEST_FULFILLED });
    dispatch({ type: USER_PREFERENCES.DELETE_PROFILE_PIC, tabId });
  } catch (e) {
    raiseError(USER.GENERIC_REQUEST_FULFILLED, e.response.data);
  }
};

const getProfilePic = (tabId, userId) => async (dispatch) => {
  dispatch({ type: USER_PREFERENCES.FETCH_PROFILE_PIC });
  const apiEndpoint = `${BASE_URL}/api/Users/${userId}/ProfilePic`;
  try {
    const res = await axios.get(apiEndpoint);
    if (res.data.message) {
      raiseWarning(USER_PREFERENCES.FETCH_PROFILE_PIC_REJECTED, res.data.message);
      return;
    }
    dispatch({
      type: USER_PREFERENCES.FETCH_PROFILE_PIC_FULFILLED,
      tabId,
      profilePicFilename: res.data.filename,
      profilePicRelPath: res.data.relPath,
      profilePicDataUrl: res.data.dataUrl,
    });
  } catch (err) {
    raiseError(USER_PREFERENCES.FETCH_PROFILE_PIC_REJECTED, err.response.data);
  }
};

const uploadProfilePic = (tabId, userId, formData) => async (dispatch) => {
  dispatch({ type: USER_PREFERENCES.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Users/${userId}/ProfilePic`;
  try {
    const res = await axios.post(apiEndpoint, formData);
    if (res.data.message) {
      raiseWarning(USER_PREFERENCES.GENERIC_REQUEST_REJECTED, res.data.message);
      return;
    }
    dispatch({ type: USER_PREFERENCES.GENERIC_REQUEST_FULFILLED });
    dispatch({
      type: USER_PREFERENCES.FETCH_PROFILE_PIC_FULFILLED,
      tabId,
      profilePicFilename: res.data.filename,
      profilePicRelPath: res.data.relPath,
      profilePicDataUrl: res.data.dataUrl,
    });
    return res.data;
  } catch (err) {
    raiseError(USER_PREFERENCES.GENERIC_REQUEST_REJECTED, err.response.data);
  }
};

/**
 * Get user data with user role and unit. Provide tabId if fetched data is to be
 * stored in redux store.
 * @param {Number} userId
 * @param {Number} tabId - optional; provide if fetched data is to be stored in redux store
 * @returns
 */
const getInfoById =
  (userId, tabId = null) =>
  async (dispatch) => {
    dispatch({ type: USER.FETCH_USER });
    const apiEndpoint = `${BASE_URL}/api/Users/${userId}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({ type: USER.FETCH_USER_FULFILLED, tabId, userDetails: res.data });
      return res.data;
    } catch (err) {
      raiseError(USER.FETCH_USER_REJECTED, err.response.data);
    }
  };

const getInfoByEmployeeNo =
  (employeeNo, tabId = null) =>
  async (dispatch) => {
    dispatch({ type: USER.FETCH_USER });
    const apiEndpoint = `${BASE_URL}/api/Users/ByEmployeeNo/${employeeNo}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({ type: USER.FETCH_USER_FULFILLED, tabId, userDetails: res.data });
      return res.data;
    } catch (err) {
      raiseError(USER.FETCH_USER_REJECTED, err.response.data);
    }
  };

const getUsersPaged = (tabId, pageNo, pageSize, resetList, filters) => (dispatch) =>
  new Promise(async (resolve) => {
    dispatch({ type: USER.FETCH_ALL_USERS });
    filters = filters || emptyUserFilters;
    const apiEndpoint =
      BASE_URL +
      `/api/Users/GetPaged?pageNo=${pageNo}&pageSize=${pageSize}&${qs.stringify(
        filters
      )}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({
        type: USER.FETCH_ALL_USERS_PAGED_FULFILLED,
        tabId,
        userList: res.data.userList,
        resetList,
      });
      resolve(res.data.pageCount);
    } catch (e) {
      const errorId = getUUID();
      const type = USER.FETCH_ALL_USERS_PAGED_REJECTED;
      console.error(e);
      const newError = { errorId, type, error: e.response.data };
      actionScripts.ADD_ERROR.run(newError);
    }
  });

const getNextChunk =
  (tabId, filters = null) =>
  async (dispatch, getState) => {
    dispatch({ type: USER.FETCH_ALL_USERS });

    const existingCount = (getState().users.openUserListsV2[tabId]?.data || []).length;
    filters = filters || emptyUserFilters;
    const filtersQs = qs.stringify(filters);
    const apiEndpoint = `${BASE_URL}/api/Users/ListNext/AfterSkipping/${existingCount}?${filtersQs}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({
        type: USER.FETCH_ALL_USERS_PAGED_FULFILLED_V2,
        tabId,
        userList: res.data.userList,
        isLast: res.data.isLast,
        searchString: filters?.searchString ?? "",
      });
    } catch (e) {
      raiseError(USER.FETCH_ALL_USERS_PAGED_REJECTED_V2, e.response.data);
    }
  };

const refreshExistingChunk = () => async (dispatch, getState) => {
  dispatch({ type: USER.REFRESH_ALL_USERS_V2 });
  const openUserLists = getState().users.openUserListsV2;
  for (const tabId in openUserLists) {
    const existingCount = (openUserLists[tabId].data || []).length;
    const existingFilters = openUserLists[tabId].filters || emptyUserFilters;
    const filtersQs = qs.stringify(existingFilters);
    const apiEndpoint = `${BASE_URL}/api/Users/ListUpTo/${existingCount}?${filtersQs}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({
        type: USER.REFRESH_ALL_USERS_V2_FULFILLED,
        tabId,
        userList: res.data.userList,
      });
    } catch (e) {
      raiseError(USER.REFRESH_ALL_USERS_V2_REJECTED, e.response.data);
    }
  }
};

const getProjectRoles = (userId) => async (dispatch) => {
  dispatch({ type: USER.FETCH_PROJECT_ROLES });
  const apiEndpoint = `${BASE_URL}/api/Users/${userId}/ProjectRoles`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: USER.FETCH_PROJECT_ROLES_FULFILLED, payload: res.data });
    return res.data;
  } catch (e) {
    dispatch({ type: USER.FETCH_PROJECT_ROLES_REJECTED });
    raiseError(USER.FETCH_PROJECT_ROLES_REJECTED, e.response.data);
  }
};

// Only use for non-paged lists. For paging, use getUsersPaged instead.
const getUserSuggestions = (tabId, searchString) => (dispatch, getState) =>
  new Promise(async (resolve) => {
    dispatch({ type: USER.FETCH_USER_SUGGESTIONS });

    const apiEndpoint =
      BASE_URL + "/api/Users/GetUsersForAutocomplete/?searchString=" + searchString;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({
        type: USER.FETCH_USER_SUGGESTIONS_FULFILLED,
        tabId,
        userList: res.data.userList,
        searchString,
      });
      resolve(1);
    } catch (err) {
      const errorId = getUUID();
      const type = USER.FETCH_USER_SUGGESTIONS_REJECTED;
      const newError = { errorId, type, error: err.response.data };
      actionScripts.ADD_ERROR.run(newError);
    }
  });

/**
 * Fetches all SUPTEBE's and SENTEBE's in the provided district, along
 * with all SUPTEBE's and SENTEBE's whose unitIds are null.
 * @param {number} dist
 * @returns {UserDto[]}
 */
const getAllManagers = (dist) => async (dispatch) => {
  dispatch({ type: USER.FETCH_ALL_MANAGERS });
  const apiEndpoint = `${BASE_URL}/api/Users/ManagersForDistrict/${dist}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: USER.FETCH_ALL_MANAGERS_FULFILLED });
    return res.data;
  } catch (err) {
    raiseError(USER.FETCH_ALL_MANAGERS_REJECTED, err.response.data);
  }
};

/**
 * Fetches all user roles
 * @returns {function(function)}
 */
const getUserRoles = () => async (dispatch) => {
  dispatch({ type: USER.FETCH_USER_ROLES });
  const apiEndpoint = `${BASE_URL}/api/UserRole/List`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: USER.FETCH_USER_ROLES_FULFILLED,
      userRoles: res.data,
    });
    return res.data;
  } catch (e) {
    raiseError(USER.FETCH_USER_ROLES_REJECTED, e.response.data);
  }
};

const getNotifications = (userId) => (dispatch) => {
  dispatch({ type: NOTIFICATION.FETCH_NOTIFICATIONS });
  const apiEndpoint = BASE_URL + "/api/Users/GetNotifications/?userId=" + userId;
  axios
    .get(apiEndpoint)
    .then((response) => {
      dispatch({
        type: NOTIFICATION.FETCH_NOTIFICATIONS_FULFILLED,
        payload: response.data,
      });
    })
    .catch((err) => {
      const errorId = getUUID();
      const type = NOTIFICATION.FETCH_NOTIFICATIONS_REJECTED;
      const newError = { errorId, type, error: err.response.data };
      actionScripts.ADD_ERROR.run(newError);
    });
};

const dismissNotification = (notificationId) => async (dispatch) => {
  try {
    await axios.post(`${BASE_URL}/api/Users/DismissNotification/${notificationId}`);
    dispatch({
      type: NOTIFICATION.DISMISS_NOTIFICATION,
      notificationId,
    });
  } catch (err) {
    console.error("Error dismissing notification", err);
  }
};

const dismissAllNotifications = (notificationIds) => async (dispatch) => {
  try {
    //await axios.post(`${BASE_URL}/api/Users/DismissNotifications`, { notificationIds });
    dispatch({
      type: NOTIFICATION.BATCH_DISMISS_NOTIFICATIONS,
      notificationIds,
    });
  } catch (err) {
    console.error("Error dismissing notifications", err);
  }
};

const emptyUserFilters = {
  district: null,
  unitId: null,
  searchString: "",
};

const userActions = {
  add,
  addSignature,
  deleteProfilePic,
  deleteSignature,
  dismissNotification,
  dismissAllNotifications,
  getAppSettings,
  getAuth,
  getNotifications,
  getAllManagers,
  getInfoById,
  getInfoByEmployeeNo,
  getName,
  getNextChunk,
  getProjectRoles,
  getProfilePic,
  getSignature,
  getUserRoles,
  getUsersPaged,
  getUserSuggestions,
  login,
  logout,
  refreshExistingChunk,
  update,
  updateSignature,
  uploadProfilePic,
};

export default userActions;
